import type { Navigator, Screen } from "../app/screen";
import { Copy } from "../theme.generated";
import { floatWordmark, mountBackdrop, type Backdrop } from "../fx/backdrop";
import { animatePlates, type PlateAnimator } from "../fx/plate";
import { audio } from "../fx/audio";
import { loadSettings, toggleSettings } from "../ui/settings";
import { TENT } from "../links";

/**
 * THE MENU. The base screen: everything opens on top of it, it opens on top of nothing.
 *
 * ⭐ **LAYOUT COMES FROM THE FROZEN APPROVAL** (`art-src/menu-redesign/APPROVED/menu_APPROVED_ORIGINAL.png`):
 * wordmark and tagline high and centred, three plates in a column left of centre, the two quiet
 * links below them, and a footer row with sign-out at one end and the utility links at the other.
 *
 * ⛔ **COLOUR DOES NOT COME FROM THAT PNG, AND THIS IS THE TRAP THE THEME FILE ALREADY WARNS ABOUT:**
 * *"THE MOCK OWNS LAYOUT AND ROLE ASSIGNMENT. RevivalTheme OWNS COLOUR. Sampling a colour out of that
 * PNG is reading the wrong authority, however approved the PNG is."* The approval's wordmark samples
 * as #E6B579; that is the stale mock hue, and the locked palette is Ember + Aurora. Confirmed by
 * Alex 2026-08-14: *"no we are not using Vendettas Gold brand. We have a pallete and redesign
 * already done for this game."* The wordmark is `Display`.
 *
 * ⚠️ **THE TAGLINE IS `Copy.Tagline`, NEVER A LITERAL.** It has one owner precisely because it went
 * stale once: "A CARD GAME OF THE DEEP NORTH" stopped being true when the lore established a
 * continental setting, and a second copy is how the old words survive.
 */
export const MenuScreen: Screen = {
  id: "menu",

  // ⭐ The one legitimate base screen. Everything else must offer a way out.
  isBase: true,

  mustOffer: ["PLAY", "CAMPAIGN", "DUELS", "DECKS", "PROFILE"],

  async mount(host: HTMLElement, nav: Navigator): Promise<void> {
    const el = document.createElement("main");
    el.className = "menu";

    const brand = document.createElement("div");
    brand.className = "menu__brand";
    const wordmark = document.createElement("h1");
    wordmark.className = "menu__wordmark";
    wordmark.textContent = "REVIVAL";
    const tagline = document.createElement("p");
    tagline.className = "menu__tagline";
    tagline.textContent = Copy.Tagline;
    brand.append(wordmark, tagline);

    const plates = document.createElement("nav");
    plates.className = "menu__plates";
    plates.setAttribute("aria-label", "Main menu");
    // ⛔ **ALL THREE PLATES ARE IDENTICAL AND COLD. DO NOT MAKE PLAY WARM.**
    // The warm fill, the gold rim and the larger size in the approval are the HOVER state, and
    // baking them in is a mistake this project has now made twice - once in `RevivalScreen.Action`
    // and once in the first draft of this file. Alex, 2026-08-10: *"The reason PLAY on this page is
    // bigger and in a different colour is because it is being selected or hovered over. That is a
    // dynamic feature to implement."* See `src/fx/plate.ts`.
    plates.append(
      plate("PLAY", "quick match", () => nav.go("leagues")),
      // ⭐ **CAMPAIGN OPENS THE CHOOSER, NOT ONE OF THE THREE CAMPAIGNS.**
      //
      // 🚨 This line used to read `nav.go("campaigns")` with a comment claiming the Ascent was
      // "reached from inside it" - a link that was never built. So pointing the plate at ONE
      // campaign deleted the chooser AND made another campaign unreachable, from a single edit.
      // Alex: *"we are missing a page that we used to have."*
      plate("CAMPAIGN", "three campaigns", () => nav.go("campaignhub")),
      plate("DUELS", "play a friend", () => nav.go("duels")),
      // ⭐ **PRACTICE IS ITS OWN DOOR.** Alex, 2026-08-15: *"I would prefer the Practice is a
      // different button below Duels. That way it is very clear for new users instead of trying to
      // find it under the Play - Quick Match."* The skill choice was first put on the PLAY screen as
      // a property of a quick match - the right shape, the wrong place. Somebody who has just built
      // their first deck will not go reading the ladder screen for a difficulty toggle.
      plate("PRACTICE", "no stakes", () => nav.go("practice")),
    );

    const links = document.createElement("div");
    links.className = "menu__links";
    const dot = document.createElement("span");
    dot.className = "menu__dot";
    dot.textContent = "·";
    dot.setAttribute("aria-hidden", "true");
    // ⭐ **TWO QUIET LINKS, NOT THREE SHOUTING ONES.** Alex, 2026-08-15: *"This layout of DECKS
    // PROFILE and HOW TO PLAY is ugly now."* He is right and the cause is geometric: `.linkbtn` uses
    // `--step-title`, the SAME size class as a plate's title, and the row is centred on the plate
    // column (BlockCentre 40%) but three long words wide - so it spilled past both edges of the
    // column it is supposed to sit under, and read as a fourth tier of primary navigation.
    //
    // ⚠️ HOW TO PLAY IS NOT A PEER OF THESE TWO and putting it in the row was the real mistake.
    // DECKS and PROFILE are places you go back to; HOW TO PLAY is where a person who has never seen
    // a card game starts. It gets its own line, below, phrased as an invitation - which also answers
    // the complaint underneath the layout one: *"how can a player come here and know how to play"*.
    links.append(
      linkButton("DECKS", () => nav.go("decks")),
      dot,
      linkButton("PROFILE", () => nav.go("profile")),
      // ⭐ **THE WAY INTO 53 AUTHORED TUTORIAL STEPS THAT NO WEB PLAYER HAD EVER SEEN.** All three
      // tutorial routes were served and called by nothing, so a new player met a card game with no
      // explanation of Foxfire, the mulligan, a turn or an attack.
      //
      // ⚠️ **OFFERED, NOT FORCED.** Auto-running it on first visit would seize the screen from
      // somebody who arrived to play, and a tutorial a player cannot decline is one they resent.
      // The lobby tour starts here; the in-match lesson starts itself when a match says `Welcome`.
    );

    // The newcomer's door. Its own line, quieter than the links, and it says what it is FOR.
    const learn = document.createElement("div");
    learn.className = "menu__learn";
    const learnLead = document.createElement("span");
    learnLead.className = "menu__learnlead";
    learnLead.textContent = "New here?";
    const learnBtn = document.createElement("button");
    learnBtn.type = "button";
    learnBtn.className = "menu__learnbtn";
    learnBtn.dataset["label"] = "HOW TO PLAY";
    learnBtn.textContent = "HOW TO PLAY";
    // ⭐ THE RULES FIRST, THE TOUR SECOND. `help` explains the turn, Foxfire, the loyalty wheel and
    // every keyword; the lobby tour only points at buttons. A player who has never seen the game
    // needs the rules, and this link was previously the tour alone.
    learnBtn.addEventListener("click", () => { audio.play("click", 0.6); nav.go("help"); });
    learn.append(learnLead, learnBtn);

    const footer = document.createElement("footer");
    footer.className = "menu__footer";

    // ⛔ **THERE IS NO SIGN OUT, AND THAT IS DELIBERATE.** Alex asked whether it was needed and
    // agreed to remove it, 2026-08-14. Three reasons, in order of weight:
    //
    //   1. **There is no account to sign out of.** `LoginGuest` derives the player from the beta
    //      gate cookie and, in `Program.cs`'s own words, "never from anything the client sends". So
    //      the identity IS the wallet.
    //   2. **It was close to a no-op.** Clearing session storage and reloading signed the same
    //      wallet straight back in - a control that looks live and changes nothing, which is the
    //      exact class of thing this rebuild exists to delete.
    //   3. **Session lifetime belongs to the PLATFORM, not to one game.**
    //      `../VendettaSaloon/docs/PLATFORM-PROFILE-SPEC.md` makes the wallet-keyed `PlayerAccount`
    //      the single owner of identity across Vendetta. A per-game sign-out is a second owner, and
    //      the two would disagree the moment a player used it.
    //
    // If a disconnect is ever needed it belongs where the wallet is connected, not here.

    const utility = document.createElement("div");
    utility.className = "menu__utility";
    utility.append(
      // ⚠️ SHOP IS A LINK BY DESIGN, not a screen. Alex, 2026-08-13: "shop should go to our Wanted
      // site." The original disabled its own in-game shop anyway, so there is nothing to restore.
      //
      // ⭐ **IT GOES TO THE TENT, NOT THE MARKETPLACE ROOT.** Alex, 2026-08-18: *"the shop for
      // revival should go to the tent on wanted."* It pointed at the root until then, which dropped
      // the player on a page listing every collection with Revival nowhere in sight. `TENT` is the
      // ONE owner of that URL - the post-match mint link reads the same constant, and the two used
      // to disagree.
      external("SHOP", TENT),
      // ⭐ **DISCORD, NOT TELEGRAM.** Alex, 2026-08-17: *"for the Community please send players to our
      // Discord."*
      //
      // 🔴 **AND THE OLD LINK WAS NOT MERELY THE WRONG PLATFORM, IT WAS A DEAD END.** It pointed at
      // `t.me/vendettagames`, which resolves - verified 2026-08-17 - to *"Telegram: **Contact**
      // @vendettagames"*. Telegram says "Contact" for a USER ACCOUNT and "View" for a channel or
      // group, so every player who tapped COMMUNITY was offered a private message to an account
      // rather than the community. It answered 200 the whole time, which is why nothing caught it:
      // **a link being reachable is not the same as it going where it claims.**
      //
      // ⚠️ Vendetta's real Telegram is `t.me/OfficialVendettaGames` (that one does return "View"), so
      // this was a wrong handle rather than a missing platform. Do not "restore" the old value.
      external("COMMUNITY", "https://discord.gg/Fp3jGefGUE"),
      utilButton("HELP", () => nav.go("help")),
      // ⭐ **THE LONGWAKE IS THE SETTING, AND IT HAD NO SURFACE AT ALL.** Revival's differentiator is
      // that it is North American folklore rather than another western, and that lived only in a
      // design document and in art a player had to infer it from. A link here is what stops the lore
      // page joining the list of things this project SHIPPED and no screen could reach.
      utilButton("THE LONGWAKE", () => nav.go("lore")),
      // ⭐ **SETTINGS OPENS THE PANEL, IT DOES NOT NAVIGATE.** The sound controls already exist as an
      // overlay (they have to: a player changes the volume DURING a match, and leaving the board
      // would abandon the turn). Routing to a separate screen here would have been a second owner of
      // the same controls, and it was a stub - a real control pointing at "not built yet".
      utilButton("SETTINGS", () => toggleSettings()),
      utilButton("RANKS", () => nav.go("ranks")),
    );

    footer.append(utility);
    // ⭐ **ONE COLUMN, NOT THREE ABSOLUTE POSITIONS.** The plates, the two quiet links and the
    // newcomer's line were each positioned independently against the viewport - the plates by `top`,
    // the other two by their own `bottom` percentages, all tuned to a THREE-plate stack. Adding
    // PRACTICE pushed the plates down into the links (measured: plates bottom 631, links top 566).
    //
    // §3b: to add a menu entry, how many places had to change? THREE - the plate list and two
    // percentages that encode how tall the list happens to be. Now it is one: the stack flows, and
    // a fifth entry costs nothing.
    const stack = document.createElement("div");
    stack.className = "menu__stack";
    stack.append(plates, links, learn);
    el.append(brand, stack, spacer(), footer);
    host.append(el);

    // ⭐ THE BACKDROP IS A MESH, NOT AN <img>. The trees are painted into one image and the wind is a
    // per-vertex warp - see `src/fx/backdrop.ts`, which transcribes `RevivalTreeSway` exactly.
    // ⭐ THE MAIN THEME. Armed here so the very first click a player makes starts the music.
    audio.arm();
    // The player's saved volumes, from their PROFILE, applied before the theme starts.
    void loadSettings();
    audio.setMusic("menu");

    backdrop = await mountBackdrop(el);

    // ⭐ **MIST ACROSS THE SKY - A SEPARATE LAYER, SO THE TREE SWAY IS NOT TOUCHED.** Alex,
    // 2026-08-15: *"could you make the mist in the background image move across the sky? Keep the
    // trees swaying as they are, please do not change them at all."*
    //
    // ⚠️ The backdrop is ONE image warped by a vertex mesh - the sway IS that warp, weighted so only
    // the edges move. Adding drift inside it would mean touching the same vertices the sway owns,
    // which is exactly what he asked me not to do. So the mist is drawn OVER the canvas and cannot
    // reach it: three soft bands at different speeds, which is what gives the parallax.
    const mist = document.createElement("div");
    mist.className = "menu__mist";
    mist.setAttribute("aria-hidden", "true");
    for (let i = 1; i <= 3; i++) {
      const band = document.createElement("span");
      band.className = `menu__mistband menu__mistband--${i}`;
      mist.append(band);
    }
    // ⭐ **THE MOON ANSWERS THE MIST.** Alex, 2026-08-15: *"it must be noticeable when the mist
    // passes across the moon"* and *"when the mist clears a bit over the moon and is heavier over
    // the moon there should be a difference in the light of the moon."*
    //
    // Drift alone is not enough: mist over a treeline is subtle by nature, and the ONE place a
    // viewer will read it instantly is the light source it crosses. Real moonlight through mist
    // does two things at once - the core DIMS and the halo GROWS, because the light is scattered
    // rather than blocked. Doing only one of them looks like a fade.
    //
    // ⚠️ Its period matches the first mist band's, so the two stay in phase forever rather than
    // beating against each other into an occasional accidental double-dip.
    const moon = document.createElement("div");
    moon.className = "menu__moon";
    moon.setAttribute("aria-hidden", "true");
    mist.append(moon);

    // After the canvas, before the brand - it sits between the art and the words.
    el.insertBefore(mist, el.firstChild?.nextSibling ?? null);
    stopFloat = floatWordmark(wordmark);
    plateAnim = animatePlates(el);
  },

  unmount(): void {
    // ⚠️ EVERY ONE OF THESE MUST BE RELEASED. A Pixi ticker and two rAF loops that outlive their
    // screen are a leak whose symptom is the menu getting slower each time you return to it.
    backdrop?.destroy();
    backdrop = null;
    stopFloat?.();
    stopFloat = null;
    plateAnim?.stop();
    plateAnim = null;
  },
};

let backdrop: Backdrop | null = null;
let stopFloat: (() => void) | null = null;
let plateAnim: PlateAnimator | null = null;

function spacer(): HTMLElement {
  const s = document.createElement("div");
  s.setAttribute("aria-hidden", "true");
  return s;
}

/**
 * One plate.
 *
 * ⚠️ THE FACE IS A CHILD, NOT A BORDER. The plate's torn silhouette is a `clip-path`, and a CSS
 * border does not follow one. So the button itself carries the EDGE colour and an inset child
 * carries the FILL - the gap between them is the rim, which is how `RevivalPlate` draws it too
 * (edge mesh, then fill mesh inset by `EdgeWidth`).
 */
function plate(label: string, sub: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "plate";

  const face = document.createElement("span");
  face.className = "plate__face";
  face.setAttribute("aria-hidden", "true");
  b.append(face);

  // ⭐ `data-label` IS THE AFFORDANCE CHECK'S KEY. Reading `textContent` alone would concatenate the
  // plate's label and its sub-caption ("PLAYQUICK MATCH"), so a screen could declare PLAY, render
  // PLAY, and still fail. The check reads this attribute first.
  b.dataset["label"] = label;

  const l = document.createElement("span");
  l.className = "plate__label";
  l.textContent = label;

  const s = document.createElement("span");
  s.className = "plate__sub";
  s.textContent = sub;

  b.append(l, s);
  b.addEventListener("click", () => {
    audio.play("click");
    onClick();
  });
  b.addEventListener("pointerenter", () => audio.play("cardHover", 0.4));
  return b;
}

function linkButton(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "linkbtn";
  b.dataset["label"] = label;
  b.textContent = label;
  b.addEventListener("click", onClick);
  return b;
}

function utilButton(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "utilbtn";
  b.dataset["label"] = label;
  b.textContent = label;
  b.addEventListener("click", onClick);
  return b;
}

/**
 * An off-site link.
 *
 * ⚠️ `rel="noopener noreferrer"` IS A SECURITY CONTROL, not boilerplate. Without `noopener` the
 * opened page gets a handle on ours via `window.opener` and can navigate it somewhere else - a
 * phishing vector on a page that has just asked a player to connect a wallet.
 */
function external(label: string, href: string): HTMLAnchorElement {
  const a = document.createElement("a");
  a.className = "utilbtn";
  a.dataset["label"] = label;
  a.textContent = label;
  a.href = href;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  return a;
}
